import { useCallback, useEffect, useState } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { UnauthorizedError } from '../auth/authFetch';
import { Post, PostPayload, listPosts, createPost, updatePost, deletePost } from './api';
import PostList from './PostList';
import PostEditor from './PostEditor';
import DeleteModal from './DeleteModal';

// Admin URL segments that are sibling routes, not post ids.
const RESERVED_SEGMENTS = ['new', 'stats'];

// authFetch throws UnauthorizedError when a 401 can't be recovered by refresh.
function isUnauthorized(err: unknown): boolean {
  return err instanceof UnauthorizedError;
}

export default function PostsManager() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [deleteError, setDeleteError] = useState(false);
  const [deleting, setDeleting] = useState<Post | null>(null);

  // The active view is derived from the URL so editing a post is a real,
  // refreshable, shareable route (/admin/:id). Sibling routes like '/admin/new'
  // and '/admin/stats' also match the ':id' pattern, so they're resolved first
  // and win — without this, /admin/stats reads as a post id, finds nothing, and
  // silently redirects home.
  const editMatch = useMatch('/admin/:id');
  const matchedId = editMatch?.params.id;
  const isNew = matchedId === 'new';
  const editId = matchedId && !RESERVED_SEGMENTS.includes(matchedId) ? matchedId : undefined;
  const editingPost = editId ? posts.find((p) => String(p.id) === editId) : undefined;

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      setPosts(await listPosts());
    } catch (err) {
      if (isUnauthorized(err)) {
        signOut();
        return;
      }
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [signOut]);

  useEffect(() => {
    load();
  }, [load]);

  const goToList = useCallback(() => navigate('/admin'), [navigate]);

  // A deep link to a post that no longer exists (deleted, or a bad id) falls
  // back to the list once we know the load actually succeeded.
  useEffect(() => {
    if (editId && !loading && !loadError && !editingPost) {
      navigate('/admin', { replace: true });
    }
  }, [editId, loading, loadError, editingPost, navigate]);

  // Persist via the API, then reconcile local state with the server's truth
  // (generated slug, timestamps). Errors propagate so the editor can surface
  // them / sign out on an unrecoverable 401.
  //
  // `postId` is the row the editor was autosaving — an existing post, or one it
  // auto-created on first autosave. Either way the save promotes that row (PUT)
  // rather than creating a duplicate; a post with no autosaved row is a plain create.
  const handleSave = async (payload: PostPayload, postId?: number) => {
    const id = postId ?? editingPost?.id;
    if (id !== undefined) {
      const saved = await updatePost(id, payload);
      setPosts((prev) =>
        prev.some((p) => p.id === saved.id)
          ? prev.map((p) => (p.id === saved.id ? saved : p))
          : [saved, ...prev],
      );
    } else {
      const created = await createPost(payload);
      setPosts((prev) => [created, ...prev]);
    }
    goToList();
  };

  const confirmDelete = async (id: number) => {
    setDeleteError(false);
    try {
      await deletePost(id);
      setPosts((prev) => prev.filter((p) => p.id !== id));
      setDeleting(null);
    } catch (err) {
      if (isUnauthorized(err)) {
        signOut();
        return;
      }
      setDeleting(null);
      setDeleteError(true);
    }
  };

  const loadingView = (
    <p className="px-1 py-16 text-center font-trirong_light text-gray-500">Loading your blogs…</p>
  );

  const errorView = (
    <div className="flex flex-col items-center gap-4 px-6 py-20 text-center">
      <h2 className="font-trirong_reg text-[26px] text-gray-800">We couldn't load your blogs</h2>
      <p className="max-w-xl font-trirong_light leading-relaxed text-gray-600">
        Something went wrong reaching the server. Please check your connection and try again.
      </p>
      <button
        type="button"
        onClick={load}
        className="mt-1.5 rounded-full bg-lime-700 px-5 py-2.5 font-trirong_reg text-[#f7faec]
                   shadow-sm transition-colors hover:bg-lime-800 focus:outline-none focus:ring-2 focus:ring-lime-600/40"
      >
        Try again
      </button>
    </div>
  );

  // Editor view (new post, or editing an existing one). A new post needs no
  // loaded data; an edit waits for the post to be available (or recovers via
  // the redirect effect above if it can't be found).
  if (isNew || editId) {
    if (editId) {
      if (loading) return loadingView;
      if (loadError) return errorView;
      if (!editingPost) return loadingView; // not found — redirecting to the list
    }
    return (
      <PostEditor
        key={editId ?? 'new'}
        initialPost={editingPost}
        onSave={handleSave}
        onCancel={goToList}
      />
    );
  }

  // List view.
  if (loading) return loadingView;
  if (loadError) return errorView;

  return (
    <>
      {deleteError && (
        <p className="mb-6 rounded-lg bg-red-50 px-4 py-3 font-trirong_light text-sm text-danger">
          That blog couldn't be deleted. Please try again.
        </p>
      )}
      <PostList
        posts={posts}
        onNew={() => navigate('/admin/new')}
        onEdit={(post) => navigate(`/admin/${post.id}`)}
        onDelete={(id) => {
          setDeleteError(false);
          setDeleting(posts.find((p) => p.id === id) ?? null);
        }}
      />
      {deleting && (
        <DeleteModal post={deleting} onCancel={() => setDeleting(null)} onConfirm={confirmDelete} />
      )}
    </>
  );
}
